<template>
  <PublicPageShell>
    <!-- Toast (inline, no custom component) -->
    <transition name="toast">
      <div v-if="showToast" class="fixed z-[100] left-1/2 -translate-x-1/2 w-[calc(100%-2rem)] sm:w-[520px]"
        :style="{ top: `${topOffset}px` }" role="status" aria-live="polite">
        <div class="rounded-xl ring-1 ring-white/10 backdrop-blur-md shadow-lg overflow-hidden"
          :class="toastShellClass">
          <div class="p-4">
            <div class="flex items-start gap-3">
              <div class="mt-0.5 h-2.5 w-2.5 rounded-full" :class="toastDotClass"></div>

              <div class="min-w-0 flex-1">
                <div class="scio-text-body scio-weight-semibold text-neutral-50">{{ toastTitle }}</div>
                <div class="mt-1 scio-text-body text-neutral-200">
                  {{ toastMessage }}
                </div>
              </div>

              <button type="button" class="shrink-0 rounded-md p-1 text-neutral-200 hover:bg-white/5 transition"
                @click="hideToast" aria-label="Close">
                <svg viewBox="0 0 20 20" fill="currentColor" class="h-5 w-5">
                  <path fill-rule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clip-rule="evenodd" />
                </svg>
              </button>
            </div>

            <button type="button"
              class="mt-3 scio-text-meta scio-weight-semibold text-neutral-200 underline underline-offset-4 hover:text-neutral-50 transition"
              @click="handleToastClick">
              Dismiss
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Content -->
    <section ref="contactUsRoot" class="relative z-10 lg:h-[calc(100dvh-70px)]">
      <div class="mx-auto h-full max-w-7xl min-w-0 px-4 py-5 sm:px-6 sm:py-7">
        <div class="grid h-full min-w-0 grid-cols-1 items-start gap-6 lg:grid-cols-12 lg:gap-8">
          <!-- Left: narrative -->
          <div class="min-w-0 lg:col-span-5">
            <span class="inline-block scio-text-meta scio-weight-medium tracking-wide"
              :style="{ color: 'var(--color-scio-yellow-p2)' }">
              Contact
            </span>

            <h1 class="mt-4 scio-text-title/tight sm:text-4xl/tight scio-weight-heading text-neutral-50">
              Talk to the Scio team.
            </h1>

            <p class="mt-4 max-w-xl scio-body-1 text-neutral-200 sm:scio-text-lead">
              Whether you are exploring Scio, have a question about the product, or want to discuss how it could
              support your work, we would be glad to hear from you.
            </p>

            <div class="mt-7 grid max-w-xl grid-cols-1 gap-5 sm:grid-cols-3">
              <div>
                <div class="scio-text-meta scio-weight-semibold" :style="{ color: 'var(--color-scio-yellow-p1)' }">EXPLORE SCIO</div>
                <p class="mt-1 scio-text-meta text-neutral-300">Understand what it does and where it fits.</p>
              </div>
              <div>
                <div class="scio-text-meta scio-weight-semibold" :style="{ color: 'var(--color-scio-yellow-p2)' }">ASK A QUESTION</div>
                <p class="mt-1 scio-text-meta text-neutral-300">Talk through your programme, data or delivery context.</p>
              </div>
              <div>
                <div class="scio-text-meta scio-weight-semibold" :style="{ color: 'var(--color-scio-yellow-p3)' }">MEET THE TEAM</div>
                <p class="mt-1 scio-text-meta text-neutral-300">Arrange a conversation or demonstration.</p>
              </div>
            </div>

            <!-- Small “what to expect” card -->
            <!-- <div class="mt-8 rounded-2xl ring-1 ring-white/10 bg-white/5 backdrop-blur-sm p-6">
              <div class="scio-text-body scio-weight-semibold text-neutral-50">What happens next</div>
              <ul class="mt-3 space-y-2 scio-text-body text-neutral-200">
                <li class="flex gap-2">
                  <span class="mt-1.5 h-1.5 w-1.5 rounded-full bg-neutral-400"></span>
                  We triage your request to the right team (product / delivery / support).
                </li>
                <li class="flex gap-2">
                  <span class="mt-1.5 h-1.5 w-1.5 rounded-full bg-neutral-400"></span>
                  We reply with next steps (demo, onboarding, or follow-up questions).
                </li>
              </ul>
            </div> -->
          </div>

          <!-- Right: form -->
          <div class="min-w-0 lg:col-span-7">
            <div class="min-w-0 overflow-visible">
              <div class="pb-4">
                <div class="scio-text-body scio-weight-semibold text-neutral-50">Send us a message</div>
                <p class="mt-1 scio-text-meta text-neutral-300">Tell us how we can help and we’ll respond with next steps.</p>
              </div>

              <form class="contact-form min-w-0 py-1" @submit.prevent="submitForm" novalidate>
                <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <!-- First name -->
                  <div>
                    <label class="block scio-text-body scio-weight-semibold text-neutral-200">
                      First name <span class="text-neutral-400">*</span>
                    </label>
                    <input v-model.trim="formData.firstName" @input="clearFieldError('firstName')" type="text"
                      autocomplete="given-name" class="mt-2 w-full rounded-lg bg-neutral-950/20 text-neutral-50 placeholder:text-scio-yellow-p3/30
                             border border-white/10 px-4 py-2.5 outline-none transition
                             focus:ring-2 focus:ring-offset-0" :style="{ '--tw-ring-color': 'rgba(255,255,255,0.18)' }"
                      :class="fieldErrorClass('firstName')" placeholder="Name" />
                    <p v-if="showError('firstName')" class="mt-2 scio-text-meta text-red-300">
                      {{ errors.firstName }}
                    </p>
                  </div>

                  <!-- Last name -->
                  <div>
                    <label class="block scio-text-body scio-weight-semibold text-neutral-200">
                      Last name <span class="text-neutral-400">*</span>
                    </label>
                    <input v-model.trim="formData.lastName" @input="clearFieldError('lastName')" type="text"
                      autocomplete="family-name" class="mt-2 w-full rounded-lg bg-neutral-950/20 text-neutral-50 placeholder:text-scio-yellow-p3/30
                             border border-white/10 px-4 py-2.5 outline-none transition focus:ring-2"
                      :class="fieldErrorClass('lastName')" placeholder="Last name" />
                    <p v-if="showError('lastName')" class="mt-2 scio-text-meta text-red-300">
                      {{ errors.lastName }}
                    </p>
                  </div>

                  <!-- Job title -->
                  <div class="sm:col-span-2">
                    <label class="block scio-text-body scio-weight-semibold text-neutral-200">
                      Job title
                    </label>
                    <input v-model.trim="formData.jobTitle" type="text" autocomplete="organization-title" class="mt-1 w-full rounded-lg bg-neutral-950/20 text-neutral-50 placeholder:text-scio-yellow-p3/30
                             border border-white/10 px-4 py-2.5 outline-none transition focus:ring-2"
                      placeholder="Title" />
                  </div>

                  <!-- Company name -->
                  <div>
                    <label class="block scio-text-body scio-weight-semibold text-neutral-200">
                      Company name <span class="text-neutral-400">*</span>
                    </label>
                    <input v-model.trim="formData.companyName" @input="clearFieldError('companyName')" type="text"
                      autocomplete="organization" class="mt-2 w-full rounded-lg bg-neutral-950/20 text-neutral-50 placeholder:text-scio-yellow-p3/30
                             border border-white/10 px-4 py-2.5 outline-none transition focus:ring-2"
                      :class="fieldErrorClass('companyName')" placeholder="Company name" />
                    <p v-if="showError('companyName')" class="mt-2 scio-text-meta text-red-300">
                      {{ errors.companyName }}
                    </p>
                  </div>

                  <!-- Business email -->
                  <div>
                    <label class="block scio-text-body scio-weight-semibold text-neutral-200">
                      Business email <span class="text-neutral-400">*</span>
                    </label>
                    <input v-model.trim="formData.businessEmail" @input="clearFieldError('businessEmail')" type="email"
                      autocomplete="email" class="mt-2 w-full rounded-lg bg-neutral-950/20 text-neutral-50 placeholder:text-scio-yellow-p3/30
                             border border-white/10 px-4 py-2.5 outline-none transition focus:ring-2"
                      :class="fieldErrorClass('businessEmail')" placeholder="yourname@example.com" />
                    <p v-if="showError('businessEmail')" class="mt-2 scio-text-meta text-red-300">
                      {{ errors.businessEmail }}
                    </p>
                  </div>

                  <!-- Message -->
                  <div class="sm:col-span-2">
                    <div class="flex items-center justify-between">
                      <label class="block scio-text-body scio-weight-semibold text-neutral-200">
                        Message <span class="text-neutral-400">*</span>
                      </label>
                      <span class="scio-text-meta text-neutral-400">
                        {{ formData.message.length }}/500
                      </span>
                    </div>

                    <textarea v-model.trim="formData.message" @input="onMessageInput" rows="3" maxlength="500" class="mt-1 w-full rounded-lg bg-neutral-950/20 text-neutral-50 placeholder:text-scio-yellow-p3/30
                             border border-white/10 px-3 py-2 outline-none transition focus:ring-2 resize-none"
                      :class="fieldErrorClass('message')"
                      placeholder="Tell us what you would like to discuss."></textarea>

                    <p v-if="showError('message')" class="mt-2 scio-text-meta text-red-300">
                      {{ errors.message }}
                    </p>
                  </div>
                </div>

                <!-- Consents -->
                <div class="mt-4 space-y-2">
                  <!-- Mailing list -->
                  <label class="group flex items-start gap-3 cursor-pointer select-none">
                    <span class="mt-0.5 h-5 w-5 rounded-md ring-1 ring-white/15 bg-neutral-950/20
                             flex items-center justify-center transition group-hover:bg-white/5"
                      :class="mailingListAgreed ? 'ring-2' : ''"
                      :style="{ '--tw-ring-color': mailingListAgreed ? 'var(--color-scio-yellow-p2)' : undefined }">
                      <svg v-if="mailingListAgreed" viewBox="0 0 20 20" fill="currentColor" class="h-4 w-4"
                        :style="{ color: 'var(--color-scio-yellow-p2)' }">
                        <path fill-rule="evenodd"
                          d="M16.704 5.29a1 1 0 010 1.415l-7.25 7.25a1 1 0 01-1.414 0l-3.25-3.25a1 1 0 111.414-1.414l2.543 2.543 6.543-6.543a1 1 0 011.414 0z"
                          clip-rule="evenodd" />
                      </svg>
                    </span>

                    <span class="min-w-0">
                      <span class="block scio-text-body scio-weight-semibold text-neutral-200">
                        I would like to receive occasional updates about Scio by email.
                      </span>
                    </span>

                    <input type="checkbox" class="sr-only" :checked="mailingListAgreed" @change="toggleMailingList" />
                  </label>

                  <!-- Privacy -->
                  <label class="group flex items-start gap-3 cursor-pointer select-none">
                    <span class="mt-0.5 h-5 w-5 rounded-md ring-1 ring-white/15 bg-neutral-950/20
                             flex items-center justify-center transition group-hover:bg-white/5"
                      :class="privacyAgreed ? 'ring-2' : ''"
                      :style="{ '--tw-ring-color': privacyAgreed ? 'var(--color-scio-yellow-p2)' : undefined }">
                      <svg v-if="privacyAgreed" viewBox="0 0 20 20" fill="currentColor" class="h-4 w-4"
                        :style="{ color: 'var(--color-scio-yellow-p2)' }">
                        <path fill-rule="evenodd"
                          d="M16.704 5.29a1 1 0 010 1.415l-7.25 7.25a1 1 0 01-1.414 0l-3.25-3.25a1 1 0 111.414-1.414l2.543 2.543 6.543-6.543a1 1 0 011.414 0z"
                          clip-rule="evenodd" />
                      </svg>
                    </span>

                    <span class="min-w-0">
                      <span class="block scio-text-meta scio-weight-semibold text-neutral-200">
                        I agree to the processing of my information as described in the Privacy Notice
                        <a class="underline underline-offset-4 hover:text-neutral-50 transition" target="_blank"
                          rel="noopener noreferrer" href="https://www.jacobs.com/policies/privacy-policy">
                          (view)
                        </a>
                        <span class="text-neutral-400">*</span>
                      </span>
                    </span>

                    <input type="checkbox" class="sr-only" :checked="privacyAgreed" @change="togglePrivacyAgreed" />
                  </label>

                  <p v-if="errors.privacyAgreed && hasSubmitted" class="scio-text-meta text-red-300">
                    {{ errors.privacyAgreed }}
                  </p>
                </div>

                <!-- Submit -->
                <div class="mt-4 flex items-center justify-end gap-3">
                  <button type="button" class="rounded-lg px-4 py-2 scio-text-body scio-weight-semibold text-neutral-200 ring-1 ring-white/10
                           hover:bg-white/5 transition" @click="resetForm">
                    Reset
                  </button>

                  <button type="submit" class="rounded-lg px-5 py-2 scio-text-body scio-weight-semibold shadow-sm transition
                           focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2
                           focus-visible:ring-neutral-200/60 focus-visible:ring-offset-neutral-950"
                    :style="{ backgroundColor: 'var(--color-scio-yellow-p2)', color: '#111827' }"
                    :class="!privacyAgreed || isSubmitting ? 'opacity-60 cursor-not-allowed' : ''"
                    :disabled="!privacyAgreed || isSubmitting">
                    <span v-if="!isSubmitting">Submit</span>
                    <span v-else>Submitting…</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

      </div>
    </section>
  </PublicPageShell>
</template>

<script>
import MailingListsService from "@/services/mailing-lists.service";
import ContactMessagesService from "../../services/contact-messages.service";
import PublicPageShell from "@/common/components/layout/PublicPageShell.vue";

export default {
  name: "ContactUs",
  components: { PublicPageShell },
  data() {
    return {
      formData: {
        firstName: "",
        lastName: "",
        jobTitle: "",
        companyName: "",
        businessEmail: "",
        message: "",
      },

      mailingListAgreed: false,
      privacyAgreed: false,

      errors: {},
      hasSubmitted: false,
      resetData: false,
      isSubmitting: false,

      showToast: false,
      toastTitle: "",
      toastMessage: "",
      toastType: "info", // info | success | error
      toastTimer: null,
    };
  },
  computed: {
    // Preserve your previous top offset behaviour
    topOffset() {
      const paths = ["/", "/contact/", "/contact-us/"];
      return paths.includes(this.$route.path) ? 128 : 40;
    },
    toastShellClass() {
      // Subtle backgrounds per type
      if (this.toastType === "success") return "bg-emerald-950/40";
      if (this.toastType === "error") return "bg-red-950/40";
      return "bg-neutral-950/45";
    },
    toastDotClass() {
      if (this.toastType === "success") return "bg-emerald-300";
      if (this.toastType === "error") return "bg-red-300";
      return "bg-neutral-300";
    },
  },
  beforeUnmount() {
    if (this.toastTimer) clearTimeout(this.toastTimer);
  },
  methods: {
    // UI helpers
    showError(field) {
      return this.errors[field] && this.hasSubmitted && !this.resetData;
    },
    fieldErrorClass(field) {
      return this.showError(field) ? "ring-2 ring-red-400/70" : "";
    },
    onMessageInput() {
      this.clearFieldError("message");
      // Optional: hard trim to 500
      if (this.formData.message.length > 500) {
        this.formData.message = this.formData.message.slice(0, 500);
      }
    },

    toggleMailingList() {
      this.mailingListAgreed = !this.mailingListAgreed;
    },
    togglePrivacyAgreed() {
      this.privacyAgreed = !this.privacyAgreed;
      if (this.privacyAgreed && this.errors.privacyAgreed) delete this.errors.privacyAgreed;
    },

    // Validation
    validateForm() {
      const errors = {};
      if (!this.formData.firstName) errors.firstName = "This field is required";
      if (!this.formData.lastName) errors.lastName = "This field is required";
      if (!this.formData.companyName) errors.companyName = "This field is required";

      if (!this.formData.businessEmail) {
        errors.businessEmail = "This field is required";
      } else {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const email = this.formData.businessEmail.trim();
        if (!emailRegex.test(email) || email.toLowerCase() === "jon.doe@mail.com") {
          errors.businessEmail = "Please enter a valid email address";
        }
      }

      if (!this.formData.message) errors.message = "This field is required";
      if (!this.privacyAgreed) errors.privacyAgreed = "This field is required";

      this.errors = errors;
      return Object.keys(errors).length === 0;
    },

    clearFieldError(field) {
      this.resetData = false;
      if (this.errors[field] && this.formData[field]) delete this.errors[field];
    },

    // Toast
    fireToast({ title, message, type = "info", timeoutMs = 5000 }) {
      this.toastTitle = title;
      this.toastMessage = message;
      this.toastType = type;
      this.showToast = true;

      if (this.toastTimer) clearTimeout(this.toastTimer);
      this.toastTimer = setTimeout(() => {
        this.showToast = false;
      }, timeoutMs);
    },
    hideToast() {
      this.showToast = false;
    },
    handleToastClick() {
      this.showToast = false;
    },

    resetForm() {
      this.hasSubmitted = false;
      this.resetData = true;
      this.errors = {};
      this.isSubmitting = false;

      this.formData = {
        firstName: "",
        lastName: "",
        jobTitle: "",
        companyName: "",
        businessEmail: "",
        message: "",
      };
      this.privacyAgreed = false;
      this.mailingListAgreed = false;
    },

    // Submit
    async submitForm() {
      this.hasSubmitted = true;
      this.resetData = false;

      if (!this.validateForm()) {
        const errorFields = Object.keys(this.errors).map((field) => {
          switch (field) {
            case "firstName": return "First name";
            case "lastName": return "Last name";
            case "companyName": return "Company name";
            case "businessEmail": return "Business email";
            case "message": return "Message";
            case "privacyAgreed": return "Privacy agreement";
            default: return field;
          }
        });

        this.fireToast({
          title: "Required fields missing or invalid",
          message: `Please check: ${errorFields.join(", ")}`,
          type: "error",
        });
        return;
      }

      this.isSubmitting = true;

      const payload = {
        id: 0,
        firstName: this.formData.firstName,
        lastName: this.formData.lastName,
        jobTitle: this.formData.jobTitle,
        companyName: this.formData.companyName,
        businessEmail: this.formData.businessEmail,
        message: this.formData.message,
      };

      const contactDetails = {
        customerName: this.formData.firstName,
        customerLastName: this.formData.lastName,
        customerJobTitle: this.formData.jobTitle,
        customerCompanyName: this.formData.companyName,
        customerEmail: this.formData.businessEmail,
        messageContent: this.formData.message,
        mailingListConsent: this.mailingListAgreed,
      };

      try {
        if (this.mailingListAgreed) {
          await MailingListsService.addToMailingList(payload);
        }
        await ContactMessagesService.sendEmail(contactDetails);
      } catch (e) {
        console.error("Error submitting contact form:", e);
        this.isSubmitting = false;

        this.fireToast({
          title: "Submission failed",
          message: "There was an error submitting your form. Please check your details and try again.",
          type: "error",
        });
        return;
      }

      this.isSubmitting = false;
      this.resetForm();

      // Scroll this component to top
      this.$nextTick(() => {
        if (this.$refs.contactUsRoot) {
          this.$refs.contactUsRoot.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      });

      this.fireToast({
        title: "Contact form submitted",
        message: "Thank you for your enquiry. We will get back to you soon.",
        type: "success",
      });
    },
  },
};
</script>

<style scoped>
.contact-form input,
.contact-form textarea {
  box-sizing: border-box;
  min-width: 0;
  max-width: 100%;
}

.toast-enter-active,
.toast-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: translate(-50%, -6px);
}
</style>
